// useStepAudioContext.js (Vue 3 compatible)

const audioCtx = new(window.AudioContext || window.webkitAudioContext)();
const audioBuffers = new Map(); // Cache for decoded buffers
const delay = ms => new Promise(resolve => setTimeout(resolve, ms))
let isMuted = false;
let currentSource = null;
// Every clip that is playing. stop() silences all of them, not just the
// last one started: a tour's narration and the near-silent unlock clip
// (play(url, 11)) are fetched and decoded in parallel, so whichever
// finished last used to be the only one stop() knew about — the nMonte
// walkthrough's narration outlived its Cancel button that way (2026-09-25).
const activeSources = new Set();
// Bumped by stop(): a play() still fetching, decoding or waiting out its
// delay at that point never starts.
let generation = 0;
// Schedule every clip a beat ahead of "now": a source started at exactly
// currentTime on a context that has just resumed loses its first tens of
// milliseconds, which clipped the opening word of walkthrough narration.
const LEAD_IN = 0.12; // seconds

export default {
    install(app) {
        app.config.globalProperties.$stepAudio = {
            isMuted: () => isMuted,
            mute: () => {
                isMuted = true;
            },
            unmute: () => {
                isMuted = false;
            },

            unlockAudioContext() {
                if (audioCtx.state === 'suspended') {
                    audioCtx.resume();
                }
            },

            async preload(url) {
                if (audioBuffers.has(url)) return audioBuffers.get(url);
                const res = await fetch(url);
                const arrayBuffer = await res.arrayBuffer();
                const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
                audioBuffers.set(url, audioBuffer);
                return audioBuffer;
            },
            async play(url, delaytime = 0) {
                console.log('play', url, delaytime);
                if (isMuted) return;
                const gen = generation;
                try {
                    // A suspended context swallows the first tens of
                    // milliseconds after start(): resume() is asynchronous
                    // and unlockAudioContext() never waited for it. Wait
                    // here so the clip's first word is not clipped.
                    if (audioCtx.state !== 'running') {
                        try { await audioCtx.resume(); } catch (err) { /* autoplay policy: play() below will warn */ }
                    }
                    const buffer = await this.preload(url);
                    if (gen !== generation) return; // stopped while loading
                    const start = () => {
                        if (gen !== generation) return; // stopped during the delay
                        const source = audioCtx.createBufferSource();
                        source.buffer = buffer;
                        if (delaytime) {
                            const gainNode = audioCtx.createGain();
                            // 11 is the audio-unlock idiom: a clip played on
                            // the click gesture at (near) zero gain, never
                            // meant to be heard.
                            gainNode.gain.value = delaytime == 11 ? 0.001 : 0.5;
                            console.log('gain', gainNode.gain.value);
                            source.connect(gainNode);
                            gainNode.connect(audioCtx.destination);
                        } else {
                            source.connect(audioCtx.destination);
                        }
                        source.onended = () => {
                            activeSources.delete(source);
                            if (currentSource === source) currentSource = null;
                        };
                        activeSources.add(source);
                        currentSource = source;
                        source.start(audioCtx.currentTime + LEAD_IN);
                    };
                    if (delaytime) {
                        setTimeout(start, delaytime);
                    } else {
                        start();
                    }
                } catch (err) {
                    console.warn('Audio playback failed:', err);
                }
            },

            stop() {
                generation++;
                activeSources.forEach(source => {
                    try {
                        source.stop(0);
                        source.disconnect();
                    } catch (err) {
                        console.warn('Audio stop failed:', err);
                    }
                });
                activeSources.clear();
                currentSource = null;
            },

            pause() {
                if (audioCtx.state === 'running') {
                    audioCtx.suspend();
                }
            },

            resume() {
                if (audioCtx.state === 'suspended') {
                    audioCtx.resume();
                }
            },

            clearCache() {
                audioBuffers.clear();
            }
        };
    }
};
